import { useEffect, useState } from "react";

import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import TimerIcon from "@mui/icons-material/Timer";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type Clocking,
  clockIn,
  clockOut,
  getCurrentClocking,
} from "@/api/clocking";

function fmtElapsed(startIso: string, now: number): string {
  const start = new Date(startIso).getTime();
  const secs = Math.max(0, Math.floor((now - start) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Compact widget that shows the currently-open clocking (with a live timer)
 * and a one-click "clock out" button. When no clocking is open AND the
 * caller passes a ``todayAssignmentId``, a "clock in" button is offered.
 */
export function ClockingWidget({
  todayAssignmentId,
}: {
  todayAssignmentId?: string | null;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());

  const current = useQuery({
    queryKey: ["clocking-current"],
    queryFn: getCurrentClocking,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  // 1-second timer only while a clocking is open
  useEffect(() => {
    if (!current.data) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [current.data]);

  const clockInMutation = useMutation({
    mutationFn: (assignmentId: string) => clockIn(assignmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clocking-current"] }),
  });

  const clockOutMutation = useMutation({
    mutationFn: (assignmentId: string) => clockOut(assignmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clocking-current"] }),
  });

  if (current.isLoading) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <CircularProgress size={20} />
        </CardContent>
      </Card>
    );
  }

  const open: Clocking | null = current.data ?? null;

  if (open) {
    const planned = open.planned_minutes;
    return (
      <Card variant="outlined" sx={{ mb: 2, borderColor: "success.main" }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                <TimerIcon
                  fontSize="small"
                  sx={{ verticalAlign: "middle", mr: 1, color: "success.main" }}
                />
                {t("clocking.activeTitle")}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  color="success"
                  label={fmtElapsed(open.clock_in_at, now)}
                  sx={{ fontFamily: "monospace", fontWeight: 700 }}
                />
                {planned !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {t("clocking.planned", { minutes: planned })}
                  </Typography>
                )}
              </Stack>
            </Box>
            <Button
              variant="contained"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={() => clockOutMutation.mutate(open.assignment_id)}
              disabled={clockOutMutation.isPending}
            >
              {t("clocking.clockOut")}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (!todayAssignmentId) return null;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              <TimerIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 1 }}
              />
              {t("clocking.idleTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("clocking.idleSubtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="success"
            startIcon={<LoginIcon />}
            onClick={() => clockInMutation.mutate(todayAssignmentId)}
            disabled={clockInMutation.isPending}
          >
            {t("clocking.clockIn")}
          </Button>
        </Stack>
        {clockInMutation.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {String(
              (clockInMutation.error as { response?: { data?: { detail?: string } } })
                .response?.data?.detail ?? "error",
            )}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
